/**
 * GetStartedStep Component
 *
 * Renders a single expandable step in the onboarding checklist.
 * Shows completion state and expands to reveal detailed directions.
 */

import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { cn, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@scio3/ui';
import type { CompanyPayment } from '@scio3/shared-types';
import { SafeHtml } from '@/components/shared/SafeHtml';
import type { StepState } from '../types';
import { isFlagCompleted } from '../constants';

interface GetStartedStepProps {
  step: StepState;
  index: number;
  isActive: boolean;
  onToggle: () => void;
  payment?: CompanyPayment;
}

export function GetStartedStep({ step, index, isActive, onToggle, payment }: GetStartedStepProps) {
  const { t } = useTranslation();

  // Check if a direction group is completed
  const isGroupCompleted = (settingKey: keyof CompanyPayment): boolean => {
    if (!payment) return false;
    const value = payment[settingKey] as string | undefined;
    return isFlagCompleted(value);
  };

  return (
    <Collapsible open={isActive} onOpenChange={onToggle}>
      <div className={cn('rounded-md transition-colors', isActive && 'bg-muted/50 dark:bg-card')}>
        {/* Step header - always visible */}
        <CollapsibleTrigger asChild>
          <button
            data-testid={`checklist-step-${step.id}-btn`}
            type="button"
            className={cn(
              'flex w-full items-center gap-3 p-2 text-left',
              'hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md',
            )}
            aria-label={t(step.titleKey)}
          >
            {/* Step indicator: checkmark if completed, number if pending */}
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                step.completed
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-muted-foreground/50 text-muted-foreground',
              )}
            >
              {step.completed ? <Check className="h-3.5 w-3.5" /> : index + 1}
            </div>

            {/* Step title */}
            <div className="min-w-0 flex-1">
              <span
                className={cn(
                  'text-sm font-medium',
                  step.completed && 'text-muted-foreground line-through',
                )}
              >
                {t(step.titleKey)}
              </span>
              {!step.required && (
                <span className="ml-2 text-xs text-muted-foreground">
                  ({t('getStarted.optional')})
                </span>
              )}
            </div>

            {/* Chevron indicator */}
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                isActive && 'rotate-180',
              )}
            />
          </button>
        </CollapsibleTrigger>

        {/* Expandable directions */}
        <CollapsibleContent>
          <div className="pb-2 pl-11 pr-2">
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              {/* Direction groups (with separate completion tracking) */}
              {step.directionGroups?.map((group) =>
                group.directions.map((direction) => (
                  <li
                    key={direction}
                    data-testid={`direction-${group.settingKey}`}
                    className={cn(
                      'flex items-start gap-2',
                      isGroupCompleted(group.settingKey) && 'line-through opacity-60',
                    )}
                  >
                    <span className="mt-0.5">&bull;</span>
                    <SafeHtml html={t(direction)} />
                  </li>
                )),
              )}

              {/* Simple directions (share step completion) */}
              {step.directions?.map((direction) => (
                <li
                  key={direction}
                  className={cn(
                    'flex items-start gap-2',
                    step.completed && 'line-through opacity-60',
                  )}
                >
                  <span className="mt-0.5">&bull;</span>
                  <SafeHtml html={t(direction)} />
                </li>
              ))}
            </ul>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
